'use client'

import { useEffect, useMemo, useState } from 'react'

import Link from 'next/link'
import { useParams, usePathname } from 'next/navigation'

// Third-party Imports
import classnames from 'classnames'

// MUI Imports
import Breadcrumbs from '@mui/material/Breadcrumbs'
import IconButton from '@mui/material/IconButton'
import MuiLink from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

// Icon Imports
import RefreshRounded from '@mui/icons-material/RefreshRounded'

// Component Imports
import NavToggle from './NavToggle'
import ModeDropdown from '@components/layout/shared/ModeDropdown'
import UserDropdown from '@components/layout/shared/UserDropdown'

// Util Imports
import { verticalLayoutClasses } from '@layouts/utils/layoutClasses'
import { useAuth } from '@/contexts/auth-context'
import { centrosApi } from '@/lib/api'
import type { Centro } from '@/types/gia'

const labelMap: Record<string, string> = {
  dashboard: 'Inicio',
  settings: 'Configuración',
  modules: 'Módulos',
  roles: 'Roles',
  permissions: 'Permisos',
  'user-permissions': 'Permisos por usuario',
  'centro-modules': 'Módulos por centro',
  'dieta-types': 'Tipos de dieta',
  'absence-types': 'Tipos de ausencia',
  'import-export': 'Importar / Exportar',
  personas: 'Personas',
  dietas: 'Dietas',
  ausencias: 'Ausencias',
  centros: 'Centros',
  usuarios: 'Usuarios',
  'call-logs': 'Llamadas',
  diuresis: 'Diuresis',
  perfil: 'Perfil',
  pdis: 'PDIs'
}

const toLabel = (segment: string) => {
  if (labelMap[segment]) return labelMap[segment]

  return segment
    .replace(/-/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
}

const NavbarContent = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const pathname = usePathname()
  const params = useParams()
  const locale = typeof params.lang === 'string' ? params.lang : 'es'
  const { user, permissions, centros, impersonatedCentroId, impersonateCentro } = useAuth()
  const [centroOptions, setCentroOptions] = useState<Centro[]>([])
  const segments = pathname.split('?')[0].split('/').filter(Boolean)
  const pathSegments = segments.slice(1)

  const crumbs = pathSegments.map((segment, index) => {
    const href = `/${locale}/${pathSegments.slice(0, index + 1).join('/')}`

    return { segment, href, label: toLabel(segment) }
  })

  const filteredCrumbs = crumbs.filter(crumb => crumb.segment !== 'dashboard')
  const mobileTitle = filteredCrumbs.length ? filteredCrumbs[filteredCrumbs.length - 1].label : 'Inicio'
  const canRefreshMenu = permissions.includes('*') || user?.rol_legacy === 'admin'

  const hasSanitaryRole = useMemo(
    () => centros.some(centro => centro.role_code === 'servicio_sanitario' && centro.centro_id === null),
    [centros]
  )

  const directorCentros = useMemo(
    () =>
      centros.filter(
        centro =>
          centro.centro_id !== null &&
          (centro.role_code.toLowerCase().includes('director') || centro.role_name.toLowerCase().includes('director'))
      ),
    [centros]
  )

  const hasMultiDirectorCentros = directorCentros.length > 1
  const canImpersonate = user?.rol_legacy === 'admin' || permissions.includes('*') || hasSanitaryRole || hasMultiDirectorCentros
  const impersonateLabel = hasSanitaryRole || hasMultiDirectorCentros ? 'Centro' : 'Ver como'

  useEffect(() => {
    if (!canImpersonate) return

    if (hasMultiDirectorCentros && !(user?.rol_legacy === 'admin' || permissions.includes('*') || hasSanitaryRole)) {
      setCentroOptions(
        directorCentros.map(centro => ({
          id: centro.centro_id as number,
          nombre: centro.centro_nombre
        }))
      )

      return
    }

    const loadCentros = async () => {
      try {
        const response = await centrosApi.listAll()

        setCentroOptions(response)
      } catch (error) {
        console.error(error)
      }
    }

    loadCentros()
  }, [canImpersonate, hasMultiDirectorCentros, hasSanitaryRole, directorCentros, permissions, user?.rol_legacy])

  const handleRefreshMenu = () => {
    if (typeof window === 'undefined') return

    // Best-effort cleanup of cached menu data
    localStorage.removeItem('gia-menu')
    localStorage.removeItem('menu-data')
    localStorage.removeItem('menu-cache')
    sessionStorage.removeItem('menu-data')

    window.location.reload()
  }

  return (
    <div
      className={classnames(verticalLayoutClasses.navbarContent, 'flex is-full gap-4', {
        'flex-col items-start': isMobile,
        'items-center justify-between': !isMobile
      })}
    >
      <div className={classnames('flex items-center gap-4', { 'w-full justify-between': isMobile })}>
        <NavToggle />
        {isMobile ? (
          <Typography color='text.primary' fontWeight={600}>
            {mobileTitle}
          </Typography>
        ) : (
          <Breadcrumbs aria-label='breadcrumb'>
            <MuiLink component={Link} color='inherit' underline='hover' href={`/${locale}/dashboard`}>
              Inicio
            </MuiLink>
            {filteredCrumbs.map((crumb, index) => {
              const isLast = index === filteredCrumbs.length - 1

              if (isLast) {
                return (
                  <Typography color='text.primary' key={crumb.href}>
                    {crumb.label}
                  </Typography>
                )
              }

              return (
                <MuiLink component={Link} color='inherit' underline='hover' href={crumb.href} key={crumb.href}>
                  {crumb.label}
                </MuiLink>
              )
            })}
          </Breadcrumbs>
        )}
      </div>
      <div className={classnames('flex items-center', { 'w-full flex-wrap gap-2': isMobile })}>
        {canRefreshMenu && (
          <Tooltip title='Actualizar menú'>
            <IconButton onClick={handleRefreshMenu} size='small' sx={{ mr: 1 }}>
              <RefreshRounded fontSize='small' />
            </IconButton>
          </Tooltip>
        )}
        {canImpersonate && (
          <TextField
            select
            size='small'
            label={impersonateLabel}
            value={
              impersonatedCentroId != null && centroOptions.length > 0 && !centroOptions.some(c => c.id === impersonatedCentroId)
                ? ''
                : (impersonatedCentroId ?? '')
            }
            onChange={async event => {
              const value = event.target.value
              const centroId = value ? Number(value) : null

              await impersonateCentro(centroId)
            }}
            fullWidth={isMobile}
            sx={{ minWidth: isMobile ? '100%' : 220, mr: isMobile ? 0 : 2 }}
          >
            <MenuItem value=''>{hasSanitaryRole || hasMultiDirectorCentros ? 'Seleccionar centro' : 'Modo admin'}</MenuItem>
            {centroOptions.map(centro => (
              <MenuItem key={centro.id} value={centro.id}>
                {centro.nombre}
              </MenuItem>
            ))}
          </TextField>
        )}
        <ModeDropdown />
        <UserDropdown />
      </div>
    </div>
  )
}

export default NavbarContent
